import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { BankCardIcon, Info2Icon } from "@/shared/icons";
import { Button, Icon } from "@/shared/ui";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import FeeForm from "@/shared/widgets/FeeForm";
import FeeSuccess from "@/shared/widgets/FeeSuccess";
import PayRulesModal from "@/shared/widgets/PayRulesModal";
import useFeeHandlers from "@/domains/estate/hooks/handlers/useFeeHandlers";
import EstateFeeImage from "@/domains/estate/components/fee/EstateFeeImage";

export default function EstateFeeContainer({ onClose }: { onClose: () => void }) {
  const [isOpenRules, setIsOpenRules] = useState(false);
  const { t } = useTranslation("estate");
  const {
    isPending,
    payFeeHandler,
    estate,
    setSelectedDays,
    selectedDays,
    getFeeAmountBySelectedDays,
  } = useFeeHandlers();

  const openRules = useCallback(() => {
    setIsOpenRules(true);
  }, []);

  const closeRules = useCallback(() => {
    setIsOpenRules(false);
  }, []);

  if (!estate) return null;

  const maxDays = Math.ceil((estate.maxFee - estate.feeBalance) / estate.totalFee);
  const paidDays = estate ? Math.floor(estate.feeBalance / estate.totalFee) : 0;

  const rules = (
    <button
      type="button"
      className="flex items-center gap-1.5 text-15 font-medium text-gray/40 transition-all hover:text-gray"
      onClick={openRules}
    >
      <Info2Icon size={16} />
      <div className="underline">{t("estate.fee.rules.openButton")}</div>
    </button>
  );

  const footer =
    maxDays === 0 ? (
      <FeeSuccess days={paidDays} i18FileName="business" dataMzAccent="green">
        {rules}
      </FeeSuccess>
    ) : (
      <div className="flex flex-col gap-5">
        <div className="flex items-end justify-between">
          <div className="flex flex-col gap-1.5">
            <div className="text-20 font-bold text-[#FF4E4E]">
              {formatPrice(getFeeAmountBySelectedDays())} ₽
            </div>
            <div className="text-14 font-medium text-white/50">{t("estate.fee.payAmount")}</div>
          </div>
          {rules}
        </div>
        <Button
          className="h-[2.75rem] w-full flex items-center gap-2.5"
          onClick={payFeeHandler}
          loading={isPending}
          disabled={isPending || maxDays === 0}
        >
          <BankCardIcon size={18} />
          {t("estate.fee.pay")}
        </Button>
      </div>
    );

  return (
    <>
      <PayRulesModal
        open={isOpenRules}
        onClose={closeRules}
        container="tablet"
        i18FileName="business"
        classNameOverlay="inset-2 rounded-[1rem]"
      />
      <DeviceTabContentWrapper
        header={t("estate.tabs.fee")}
        footer={footer}
        onClose={onClose}
        className="p-0"
      >
        <EstateFeeImage id={estate.id} />
        <FeeForm
          maxDays={maxDays}
          totalFee={estate.totalFee}
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          i18fileName="estate"
        />
      </DeviceTabContentWrapper>
    </>
  );
}
